import type { LegalDocument } from "./types";

export const TERMS_AND_CONDITIONS: LegalDocument = {
  title: "Terms and Conditions",
  subtitle: "AppearInAISearch — A Product of Sarucci Inc.",
  effectiveDate: "July 4, 2026",
  lastUpdated: "July 4, 2026",
  preamble: [
    {
      kind: "notice",
      text: "IMPORTANT NOTICE. These Terms include (i) a binding individual arbitration and class action waiver in Section 13, subject to your right to opt out within thirty (30) days as described therein; (ii) a limitation of our liability in Section 10; and (iii) a disclaimer of warranties in Section 9. Please read these provisions carefully before agreeing to these Terms.",
    },
  ],
  sections: [
    {
      id: "acceptance-of-terms",
      number: "1",
      title: "Introduction and Acceptance of Terms",
      blocks: [
        {
          kind: "paragraph",
          text: "These Terms and Conditions (“Terms,” “Agreement”) constitute a legally binding agreement between you (“User,” “you,” “your”) and Sarucci Inc., a Delaware corporation (“Company,” “we,” “us,” “our”), governing your access to and use of the AppearInAISearch platform, available at appearinaisearch.com (the “Website,” “Platform,” or “Service”).",
        },
        {
          kind: "paragraph",
          text: "By accessing, browsing, registering for an account, or otherwise using the Platform, you acknowledge that you have read, understood, and agree to be bound by these Terms, together with our Privacy Policy, which is incorporated herein by reference. If you do not agree to these Terms, you must not access or use the Platform.",
        },
        {
          kind: "paragraph",
          text: "We reserve the right to modify, amend, or update these Terms at any time. For material changes, we will provide reasonable prior notice (generally at least thirty (30) days) by email or through a prominent notice on the Platform before the changes take effect. Changes will be effective on the date specified in the notice, and your continued use of the Platform after that date constitutes acceptance of the revised Terms. If you do not agree to the revised Terms, you must stop using the Platform and, if applicable, cancel your subscription before the effective date.",
        },
      ],
    },
    {
      id: "definitions",
      number: "2",
      title: "Definitions",
      blocks: [
        {
          kind: "paragraph",
          text: "For the purposes of this Agreement, the following terms shall have the meanings set forth below:",
        },
        {
          kind: "list",
          items: [
            {
              term: "“Account”",
              text: "means a registered user account on the Platform, secured by login credentials.",
            },
            {
              term: "“AI Audit”",
              text: "means the automated analysis and scoring of a hotel website across the five dimensions of AEO, GEO, AIO, Crawlability, and SXO, producing an LLM SEO Score.",
            },
            {
              term: "“AEO”",
              text: "means Answer Engine Optimization — the readiness of a website to be recommended by AI-powered answer engines.",
            },
            {
              term: "“GEO”",
              text: "means Generative Engine Optimization — optimization for generative AI search results.",
            },
            {
              term: "“AIO”",
              text: "means AI Integration — the degree to which a website is integrated with AI systems.",
            },
            {
              term: "“SXO”",
              text: "means Search Experience Optimization — the overall search experience quality of a website.",
            },
            {
              term: "“Crawlability”",
              text: "means the ability of AI bots and crawlers to access, read, and index a website’s content.",
            },
            {
              term: "“LLM SEO Score”",
              text: "means the composite score (out of 100) generated by the AI Audit feature.",
            },
            {
              term: "“LLM Visibility Testing”",
              text: "means the testing of a hotel’s mention, ranking, and recommendation status across AI platforms including but not limited to ChatGPT, Gemini, and Claude.",
            },
            {
              term: "“OTA”",
              text: "means Online Travel Agency, including but not limited to Booking.com, Expedia, and TripAdvisor.",
            },
            {
              term: "“OTA vs Direct Booking Analysis”",
              text: "means the comparative analysis of a hotel’s direct booking presence versus its OTA presence in AI-generated recommendations.",
            },
            {
              term: "“Persona Visibility”",
              text: "means the testing of a hotel’s AI visibility across different guest personas (e.g., couples, families, solo travelers, business travelers, luxury seekers).",
            },
            {
              term: "“Content”",
              text: "means all text, data, reports, scores, schema markup, recommendations, and other materials generated by or through the Platform.",
            },
            {
              term: "“User Data”",
              text: "means any data, information, or content submitted by you to the Platform, including website URLs, hotel details, and account information.",
            },
            {
              term: "“Free Plan” or “Discover Plan”",
              text: "means the complimentary tier of the Service.",
            },
            {
              term: "“Paid Plan”",
              text: "means any subscription or paid tier of the Service.",
            },
          ],
        },
      ],
    },
    {
      id: "eligibility",
      number: "3",
      title: "Eligibility",
      blocks: [
        {
          kind: "paragraph",
          text: "The Platform is intended for use by hotel managers, hotel owners, marketing agencies, and other hospitality professionals in the course of their business. By registering for or using the Platform, you represent and warrant that:",
        },
        {
          kind: "list",
          items: [
            "You are at least eighteen (18) years of age, or the age of majority in your jurisdiction if higher, and have the legal capacity and authority to enter into this Agreement;",
            "If you are using the Platform on behalf of an organization, you have the authority to bind that organization to these Terms, and references to “you” apply to both you individually and to that organization;",
            "You will provide accurate, current, and complete information during registration and throughout your use of the Platform;",
            "You are not located in, under the control of, or a national or resident of any country subject to comprehensive U.S. Government embargoes or sanctions, and you are not identified on any U.S. Government list of prohibited or restricted parties (including the Specially Designated Nationals List, the Denied Persons List, and the Entity List); and",
            "Your use of the Platform complies with all applicable local, state, national, and international laws, regulations, and industry codes.",
          ],
        },
      ],
    },
    {
      id: "account-registration",
      number: "4",
      title: "Account Registration and Security",
      blocks: [
        {
          kind: "paragraph",
          lead: "4.1 Account Creation.",
          text: "To access certain features of the Platform, you must create an Account by providing your name, email address, and other requested information. You agree to provide accurate, current, and complete information during the registration process and to update such information to keep it accurate, current, and complete.",
        },
        {
          kind: "paragraph",
          lead: "4.2 Account Security.",
          text: "You are solely responsible for maintaining the confidentiality of your login credentials and for all activities that occur under your Account. You agree to:",
        },
        {
          kind: "list",
          items: [
            "Create a strong, unique password for your Account and, where offered, enable multi-factor authentication;",
            "Not share your login credentials with any third party;",
            "Immediately notify us at support@sarucci.com of any unauthorized access to or use of your Account.",
          ],
        },
        {
          kind: "paragraph",
          lead: "4.3 Account Responsibility.",
          text: "You are responsible for all activity that occurs under your Account, whether or not authorized by you. We shall not be liable for any loss or damage arising from your failure to maintain the security of your Account credentials, except to the extent caused by our gross negligence or willful misconduct.",
        },
      ],
    },
    {
      id: "description-of-services",
      number: "5",
      title: "Description of Services",
      blocks: [
        {
          kind: "paragraph",
          text: "The Platform provides AI search optimization and answer engine optimization services designed for the hospitality industry. The Service includes the following core features:",
        },
        { kind: "subheading", text: "5.1 AI Audit" },
        {
          kind: "paragraph",
          text: "The AI Audit analyzes your hotel website across five key dimensions:",
        },
        {
          kind: "list",
          items: [
            {
              term: "AEO (Answer Engine Optimization) —",
              text: "Measures readiness for AI-powered answer engines.",
            },
            {
              term: "GEO (Generative Engine Optimization) —",
              text: "Evaluates optimization for generative AI search results.",
            },
            {
              term: "AIO (AI Integration) —",
              text: "Assesses the degree of AI system integration.",
            },
            {
              term: "Crawlability —",
              text: "Determines how effectively AI bots can read and index your site.",
            },
            {
              term: "SXO (Search Experience Optimization) —",
              text: "Evaluates overall search experience quality.",
            },
          ],
        },
        {
          kind: "paragraph",
          text: "The AI Audit produces a composite LLM SEO Score (out of 100), a prioritized issue list with step-by-step remediation guidance, and AI-generated schema markup and content suggestions.",
        },
        { kind: "subheading", text: "5.2 LLM Visibility Testing" },
        {
          kind: "paragraph",
          text: "This feature executes guest search prompts (e.g., “luxury hotels in Paris, France”) against major AI platforms — including ChatGPT, Gemini, and Claude — to determine whether your hotel is mentioned, its ranking position, and which competitors receive recommendations. This includes rank tracking over time and competitor comparison analytics.",
        },
        { kind: "subheading", text: "5.3 OTA vs Direct Booking Analysis" },
        {
          kind: "paragraph",
          text: "This feature analyzes how your hotel’s direct booking presence compares to Online Travel Agencies (OTAs such as Booking.com, Expedia, and TripAdvisor) in AI-generated answers. It identifies why OTAs may receive priority citations and provides recommendations to improve direct-booking visibility.",
        },
        { kind: "subheading", text: "5.4 Persona Visibility" },
        {
          kind: "paragraph",
          text: "This feature tests your hotel’s AI visibility across up to eight (8) distinct guest personas, including but not limited to couples, families, solo travelers, business travelers, and luxury seekers. For each persona, the Platform scores mention frequency, ranking position, fit relevance, and cross-engine consistency across multiple AI platforms.",
        },
        { kind: "subheading", text: "5.5 Beta Features" },
        {
          kind: "paragraph",
          text: "From time to time, we may offer features designated as “beta,” “preview,” “alpha,” or similar (“Beta Features”). Beta Features are provided “AS IS” and “AS AVAILABLE,” may be modified or discontinued at any time, and may be subject to additional terms. Use of Beta Features is at your sole risk, and Section 9 (Disclaimers) applies with full force.",
        },
      ],
    },
    {
      id: "plans-pricing-payment",
      number: "6",
      title: "Plans, Pricing, and Payment",
      blocks: [
        {
          kind: "paragraph",
          lead: "6.1 Free Plan (Discover Plan).",
          text: "We offer a free tier (“Discover Plan”) that provides limited access to certain Platform features, including the ability to run a basic AI Audit. The Discover Plan is provided at no cost and may be subject to usage limitations as determined by us in our reasonable discretion. We reserve the right to modify, limit, or discontinue the Discover Plan at any time; where reasonably practicable, we will provide advance notice of material changes.",
        },
        {
          kind: "paragraph",
          lead: "6.2 Paid Plans.",
          text: "We offer paid subscription plans that provide expanded access to Platform features. Pricing for Paid Plans is as displayed on the Platform at the time of purchase. We may change pricing at any time; however, price changes for existing subscribers will take effect no earlier than the next renewal period following at least thirty (30) days’ prior notice. If you do not agree to a price change, you may cancel your subscription before the change takes effect.",
        },
        {
          kind: "paragraph",
          lead: "6.3 Payment Processing.",
          text: "All payments are processed through Stripe, Inc. (“Stripe”), our third-party payment processor. By making a payment, you agree to Stripe’s terms of service and privacy policy (available at https://stripe.com/legal and https://stripe.com/privacy). We do not store your full credit card or payment card details on our servers. All payment information is transmitted directly to and handled by Stripe in accordance with PCI-DSS standards.",
        },
        {
          kind: "paragraph",
          lead: "6.4 Automatic Renewal.",
          emphasis: true,
          text: "PAID PLANS AUTOMATICALLY RENEW AT THE END OF EACH BILLING PERIOD (MONTHLY OR ANNUALLY, AS SELECTED BY YOU) AT THE THEN-CURRENT RENEWAL PRICE UNTIL YOU CANCEL. BY SUBSCRIBING, YOU AUTHORIZE US TO CHARGE THE PAYMENT METHOD ON FILE FOR ALL RECURRING FEES. For annual subscriptions, we will provide reminder notice of upcoming renewal at least fifteen (15) days before the renewal date, where required by law. You may cancel at any time through your Account settings or by contacting support@sarucci.com. This disclosure is provided in compliance with the California Automatic Renewal Law (Cal. Bus. & Prof. Code § 17600 et seq.) and similar consumer protection statutes.",
        },
        {
          kind: "paragraph",
          lead: "6.5 Cancellation.",
          text: "You may cancel your Paid Plan at any time through your Account settings or by contacting us at support@sarucci.com. Upon cancellation, your subscription will remain active until the end of the current billing period, after which it will not renew. No prorated refunds will be issued for partial billing periods, except as required by applicable law.",
        },
        {
          kind: "paragraph",
          lead: "6.6 Refunds and Consumer Cooling-Off.",
          text: "Except as required by applicable law, all fees paid are non-refundable. If you are a consumer resident in the European Union or United Kingdom, you may have a statutory right to withdraw from a purchase within fourteen (14) days without giving a reason; however, by beginning to use the Service during that period, you may lose that right in respect of services fully performed. If you believe you have been charged in error, contact us at support@sarucci.com within thirty (30) days of the charge and we will investigate in good faith.",
        },
        {
          kind: "paragraph",
          lead: "6.7 Taxes.",
          text: "All fees are exclusive of taxes. You are responsible for all applicable taxes, duties, and levies associated with your use of the Platform, except for taxes based on our net income.",
        },
        {
          kind: "paragraph",
          lead: "6.8 Late Payments.",
          text: "If any payment is not received when due, we may (i) suspend or terminate your access to the Platform, (ii) charge interest on overdue amounts at the lesser of one and one-half percent (1.5%) per month or the maximum rate permitted by law, and (iii) recover reasonable costs of collection.",
        },
      ],
    },
    {
      id: "acceptable-use",
      number: "7",
      title: "Acceptable Use Policy",
      blocks: [
        {
          kind: "paragraph",
          text: "You agree to use the Platform only for lawful purposes and in accordance with these Terms. You shall not:",
        },
        {
          kind: "list",
          items: [
            "Use the Platform for any purpose that is illegal or prohibited by these Terms;",
            "Attempt to gain unauthorized access to any portion of the Platform, other user accounts, or any systems or networks connected to the Platform;",
            "Use any automated means (including bots, scrapers, or crawlers) to access or interact with the Platform, except as expressly permitted by us;",
            "Interfere with or disrupt the integrity or performance of the Platform or its underlying infrastructure;",
            "Reverse engineer, decompile, disassemble, or otherwise attempt to derive the source code, algorithms, or scoring methodologies of the Platform, except to the extent such restriction is prohibited by applicable law;",
            "Reproduce, duplicate, copy, sell, resell, or exploit any portion of the Platform without our express written permission;",
            "Use the Platform to transmit any viruses, malware, or other harmful code;",
            "Use the Platform to develop, train, or improve any competing product, machine-learning model, or dataset;",
            "Impersonate any person or entity, or falsely state or misrepresent your affiliation with any person or entity;",
            "Use Platform output to make misleading claims about your hotel’s AI ranking or visibility;",
            "Share, transfer, or sublicense your Account access to third parties without our prior written consent;",
            "Submit any User Data that infringes, misappropriates, or violates the intellectual property, privacy, publicity, or other rights of any third party; or",
            "Use the Platform in any manner that could damage, disable, overburden, or impair the Platform.",
          ],
        },
      ],
    },
    {
      id: "intellectual-property",
      number: "8",
      title: "Intellectual Property Rights",
      blocks: [
        {
          kind: "paragraph",
          lead: "8.1 Company Intellectual Property.",
          text: "The Platform, including but not limited to its software, algorithms, scoring methodologies (including the five-dimension audit framework), user interface, design, text, graphics, logos, trademarks, trade names (including “AppearInAISearch™” and “LLM SEO Score™”), and all related intellectual property, are and shall remain the exclusive property of Sarucci Inc. or its licensors. All rights not expressly granted to you are reserved. These Terms do not grant you any right, title, or interest in the Platform or its intellectual property, except for the limited license set forth in Section 8.2.",
        },
        {
          kind: "paragraph",
          lead: "8.2 Limited License.",
          text: "Subject to your compliance with these Terms and payment of all applicable fees, we grant you a limited, non-exclusive, non-transferable, non-sublicensable, revocable license to access and use the Platform solely for your internal business purposes in connection with hotel marketing and AI search optimization.",
        },
        {
          kind: "paragraph",
          lead: "8.3 User Data License.",
          text: "You retain ownership of all User Data you submit to the Platform. You represent and warrant that you have all rights necessary to submit such User Data and to grant the licenses set forth herein. By submitting User Data, you grant us a non-exclusive, worldwide, royalty-free license to host, process, store, transmit, display, and analyze such User Data solely for the purpose of providing, securing, and supporting the Service. We may also use User Data in de-identified or aggregated form (that does not identify you or any individual) on a perpetual, irrevocable basis for the purposes of analytics, benchmarking, research, and improving our products and services.",
        },
        {
          kind: "paragraph",
          lead: "8.4 Feedback.",
          text: "If you provide any feedback, suggestions, or ideas regarding the Platform (“Feedback”), you grant us a perpetual, irrevocable, worldwide, royalty-free, sublicensable license to use, disclose, reproduce, modify, and commercialize such Feedback for any purpose, without restriction or obligation to you.",
        },
        {
          kind: "paragraph",
          lead: "8.5 Report Usage.",
          text: "Reports, scores, and recommendations generated by the Platform for your Account are provided for your internal use. You may share reports with third parties (e.g., marketing agencies working on your behalf) but may not resell or commercially redistribute them as a standalone product.",
        },
        {
          kind: "paragraph",
          lead: "8.6 DMCA Notice.",
          text: "We respect the intellectual property rights of others and expect users to do the same. If you believe that content available through the Platform infringes your copyright, please send a written notice to our designated agent at support@sarucci.com with the subject line “DMCA Notice,” including (i) a physical or electronic signature of the copyright owner or authorized agent; (ii) identification of the copyrighted work claimed to be infringed; (iii) identification of the allegedly infringing material and its location; (iv) your contact information; (v) a statement of good-faith belief that the use is not authorized; and (vi) a statement, under penalty of perjury, that the information in the notice is accurate and that you are authorized to act on behalf of the copyright owner. Under 17 U.S.C. § 512(f), any person who knowingly materially misrepresents that material is infringing may be subject to liability.",
        },
      ],
    },
    {
      id: "disclaimers",
      number: "9",
      title: "Disclaimers and Advisory Nature of Services",
      blocks: [
        {
          kind: "paragraph",
          lead: "9.1 ADVISORY NATURE.",
          emphasis: true,
          text: "YOU EXPRESSLY ACKNOWLEDGE AND AGREE THAT ALL AUDIT SCORES, LLM SEO SCORES, RANKINGS, RECOMMENDATIONS, SCHEMA SUGGESTIONS, CONTENT SUGGESTIONS, VISIBILITY ANALYSES, OTA COMPARISONS, PERSONA VISIBILITY SCORES, AND ALL OTHER OUTPUT GENERATED BY THE PLATFORM ARE ADVISORY IN NATURE ONLY. THEY DO NOT CONSTITUTE GUARANTEES OF AI RANKING, VISIBILITY, MENTION, OR RECOMMENDATION BY ANY AI PLATFORM, INCLUDING BUT NOT LIMITED TO CHATGPT, GEMINI, OR CLAUDE.",
        },
        {
          kind: "paragraph",
          lead: "9.2 No Guarantee of Results.",
          text: "We do not guarantee that:",
        },
        {
          kind: "list",
          items: [
            "Implementing the recommendations provided by the Platform will result in improved AI visibility, higher rankings, or increased bookings;",
            "Your hotel will be mentioned, ranked, or recommended by any AI platform;",
            "AI search results will remain consistent over time, as AI platforms may change their algorithms, training data, and response patterns at any time without notice;",
            "The Platform’s scoring methodology will accurately predict or reflect actual AI platform behavior.",
          ],
        },
        {
          kind: "paragraph",
          lead: "9.3 Third-Party AI Platforms.",
          text: "The Platform interacts with and analyzes results from third-party AI platforms (ChatGPT/OpenAI, Gemini/Google, Claude/Anthropic). We have no control over these platforms, their algorithms, their responses, or their availability. Changes to these platforms may affect the accuracy or availability of our Service. We are not affiliated with, endorsed by, or sponsored by any of these third-party AI platforms.",
        },
        {
          kind: "paragraph",
          lead: "9.4 “AS IS” BASIS.",
          emphasis: true,
          text: "TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, THE PLATFORM AND ALL SERVICES ARE PROVIDED ON AN “AS IS” AND “AS AVAILABLE” BASIS, WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, ACCURACY, TITLE, OR RELIABILITY, AND ANY WARRANTIES ARISING FROM COURSE OF DEALING OR USAGE OF TRADE. WE DO NOT WARRANT THAT THE PLATFORM WILL BE UNINTERRUPTED, ERROR-FREE, SECURE, OR FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.",
        },
        {
          kind: "paragraph",
          lead: "9.5 Example Data.",
          text: "Certain demonstrations, examples, or sample reports displayed on the Platform may use illustrative or example data and may not reflect actual performance metrics for any specific hotel property.",
        },
        {
          kind: "paragraph",
          lead: "9.6 Consumer Statutory Rights.",
          text: "Nothing in this Section 9 excludes or limits any implied warranty, guarantee, right, or remedy that cannot be excluded or limited under applicable law, including mandatory consumer protection laws of the European Union, United Kingdom, or other jurisdictions where you are a consumer.",
        },
      ],
    },
    {
      id: "limitation-of-liability",
      number: "10",
      title: "Limitation of Liability",
      blocks: [
        {
          kind: "paragraph",
          lead: "10.1 EXCLUSION OF DAMAGES.",
          emphasis: true,
          text: "TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL SARUCCI INC., ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, AFFILIATES, OR LICENSORS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO DAMAGES FOR LOSS OF PROFITS, REVENUE, GOODWILL, DATA, BOOKINGS, COMMISSION SAVINGS, OR OTHER INTANGIBLE LOSSES, ARISING OUT OF OR IN CONNECTION WITH:",
        },
        {
          kind: "list",
          items: [
            "Your use of or inability to use the Platform;",
            "Any reliance on audit scores, recommendations, or other output from the Platform;",
            "Changes to third-party AI platforms that affect your visibility or ranking;",
            "Unauthorized access to or alteration of your data or Account;",
            "Any third-party conduct or content on or through the Platform;",
            "Any other matter relating to the Platform.",
          ],
        },
        {
          kind: "paragraph",
          lead: "10.2 LIABILITY CAP.",
          emphasis: true,
          text: "TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, OUR TOTAL AGGREGATE LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THESE TERMS OR THE PLATFORM SHALL NOT EXCEED THE GREATER OF: (A) THE TOTAL AMOUNT PAID BY YOU TO US IN THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM; OR (B) ONE HUNDRED U.S. DOLLARS (USD $100.00).",
        },
        {
          kind: "paragraph",
          lead: "10.3 Exceptions to Limitations.",
          text: "The limitations and exclusions in Sections 10.1 and 10.2 shall not apply to: (a) your obligations to pay fees due under these Terms; (b) your indemnification obligations under Section 11; (c) either party’s liability for gross negligence, fraud, or willful misconduct; (d) your violation of our intellectual property rights or breach of Section 7 (Acceptable Use); or (e) any liability that cannot be excluded or limited under applicable law (including, where applicable, liability for death or personal injury caused by negligence).",
        },
        {
          kind: "paragraph",
          lead: "10.4 BASIS OF THE BARGAIN.",
          emphasis: true,
          text: "YOU ACKNOWLEDGE THAT THE LIMITATIONS OF LIABILITY IN THIS SECTION REFLECT A REASONABLE ALLOCATION OF RISK AND ARE A FUNDAMENTAL ELEMENT OF THE BASIS OF THE BARGAIN BETWEEN YOU AND US. THE PLATFORM WOULD NOT BE PROVIDED WITHOUT SUCH LIMITATIONS.",
        },
      ],
    },
    {
      id: "indemnification",
      number: "11",
      title: "Indemnification",
      blocks: [
        {
          kind: "paragraph",
          text: "You agree to indemnify, defend, and hold harmless Sarucci Inc., its officers, directors, employees, agents, affiliates, and licensors from and against any and all third-party claims, liabilities, damages, losses, costs, and expenses (including reasonable attorneys’ fees) arising out of or in connection with:",
        },
        {
          kind: "list",
          items: [
            "Your use of the Platform or any breach of these Terms;",
            "Any User Data you submit to the Platform;",
            "Your violation of any applicable law, regulation, or third-party right;",
            "Any claim that your use of the Platform infringes or violates the intellectual property rights of any third party;",
            "Any dispute between you and any third party arising from your use of the Platform output (e.g., claims relating to AI ranking representations made to hotel guests or stakeholders).",
          ],
        },
        {
          kind: "paragraph",
          text: "As a condition to this indemnity, we will: (i) promptly notify you in writing of the claim (provided that any failure to give such notice will not relieve you of your obligations except to the extent you are materially prejudiced); (ii) provide you with reasonable cooperation, at your expense, in the defense of the claim; and (iii) permit you to control the defense and settlement of the claim, provided that you may not settle any claim in a manner that admits liability on our behalf, imposes any obligation on us, or fails to unconditionally release us, without our prior written consent. We may participate in the defense at our own expense with counsel of our choosing.",
        },
      ],
    },
    {
      id: "term-and-termination",
      number: "12",
      title: "Term and Termination",
      blocks: [
        {
          kind: "paragraph",
          lead: "12.1 Term.",
          text: "These Terms commence on the date you first access or use the Platform and continue until terminated by either party in accordance with this Section 12.",
        },
        {
          kind: "paragraph",
          lead: "12.2 Termination by You.",
          text: "You may terminate your Account at any time by contacting us at support@sarucci.com or through your Account settings. Termination of a Paid Plan will take effect at the end of the current billing period, unless applicable law requires otherwise.",
        },
        {
          kind: "paragraph",
          lead: "12.3 Termination by Us.",
          text: "We may suspend or terminate your Account and access to the Platform: (a) immediately, upon notice, if you materially breach these Terms and, where curable, fail to cure the breach within ten (10) days after we notify you of it; (b) immediately, without notice, if we reasonably believe your use of the Platform poses a security or legal risk to us or others, or if required to do so by law; or (c) upon at least thirty (30) days’ prior notice, for convenience or if your Account has been inactive for an extended period.",
        },
        {
          kind: "paragraph",
          lead: "12.4 Effect of Termination.",
          text: "Upon termination: (a) your license to use the Platform immediately ceases; (b) you must cease all use of the Platform; (c) for a period of thirty (30) days following termination (except in cases of termination for cause under Section 12.3(a) or (b)), we will make your User Data available for export in a commonly used format upon your written request; and (d) after such period, we may delete your Account and associated data in accordance with our Privacy Policy. Any provisions of these Terms that by their nature should survive termination shall survive, including Sections 6.6–6.8, 7, 8, 9, 10, 11, 13, 14, 15, and 18.",
        },
      ],
    },
    {
      id: "dispute-resolution",
      number: "13",
      title: "Dispute Resolution",
      blocks: [
        {
          kind: "paragraph",
          lead: "13.1 Informal Resolution.",
          text: "Before initiating any formal dispute resolution proceeding, you agree to first contact us at support@sarucci.com with a written description of the dispute, the relief sought, and your contact information, and to negotiate in good faith to resolve the dispute informally for a period of at least thirty (30) days.",
        },
        {
          kind: "paragraph",
          lead: "13.2 Binding Arbitration.",
          text: "If the dispute cannot be resolved informally, you and Sarucci Inc. agree that any dispute, claim, or controversy arising out of or relating to these Terms or the Platform (“Dispute”) shall be resolved exclusively by binding arbitration administered by the American Arbitration Association (“AAA”) under its then-current rules applicable to the parties (the Commercial Arbitration Rules for business users or the Consumer Arbitration Rules where the AAA determines the user is a consumer). The arbitration shall be conducted by a single arbitrator. Unless the parties agree otherwise, the arbitration shall be conducted remotely by videoconference or, at the arbitrator’s discretion, in Wilmington, Delaware, USA. The arbitrator shall apply the governing law identified in Section 14. The language of the arbitration shall be English. Judgment on the award may be entered in any court of competent jurisdiction.",
        },
        {
          kind: "paragraph",
          lead: "13.3 Arbitration Costs.",
          text: "Each party shall bear its own attorneys’ fees and costs, except as otherwise provided in the applicable AAA rules or as required by applicable law. Filing and administrative fees will be allocated in accordance with the applicable AAA rules.",
        },
        {
          kind: "paragraph",
          lead: "13.4 30-Day Right to Opt Out.",
          emphasis: true,
          text: "YOU HAVE THE RIGHT TO OPT OUT OF THE ARBITRATION AGREEMENT AND CLASS ACTION WAIVER IN THIS SECTION 13 BY SENDING WRITTEN NOTICE OF YOUR DECISION TO OPT OUT TO support@sarucci.com WITH THE SUBJECT LINE “ARBITRATION OPT-OUT” WITHIN THIRTY (30) DAYS OF FIRST ACCEPTING THESE TERMS. THE NOTICE MUST INCLUDE YOUR NAME, ADDRESS, AND ACCOUNT EMAIL. If you opt out, Sections 13.2, 13.3, and 13.5 will not apply to you, but the remaining provisions of these Terms (including Section 14, Governing Law and Jurisdiction) will continue to apply.",
        },
        {
          kind: "paragraph",
          lead: "13.5 CLASS ACTION WAIVER.",
          emphasis: true,
          text: "YOU AND SARUCCI INC. AGREE THAT EACH PARTY MAY BRING DISPUTES AGAINST THE OTHER ONLY IN AN INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS, CONSOLIDATED, OR REPRESENTATIVE PROCEEDING. THE ARBITRATOR SHALL NOT HAVE THE AUTHORITY TO CONSOLIDATE OR JOIN CLAIMS OF MULTIPLE PARTIES OR TO CONDUCT ANY CLASS ARBITRATION. If this class action waiver is found to be unenforceable, then the entirety of this Section 13 (other than this sentence) shall be null and void.",
        },
        {
          kind: "paragraph",
          lead: "13.6 Exceptions.",
          text: "Notwithstanding the foregoing, either party may seek injunctive or other equitable relief in any court of competent jurisdiction to protect its intellectual property rights or to prevent irreparable harm pending the outcome of arbitration.",
        },
        {
          kind: "paragraph",
          lead: "13.7 Small Claims.",
          text: "Either party may elect to bring an individual Dispute in small claims court in the county of your primary residence (for individuals) or principal place of business (for entities), provided the Dispute qualifies for small claims jurisdiction.",
        },
        {
          kind: "paragraph",
          lead: "13.8 Consumer Rights.",
          text: "If you are a consumer resident in the European Union, United Kingdom, or another jurisdiction with mandatory consumer protection laws, nothing in this Section 13 will deprive you of any right you have to bring or defend proceedings in the courts of your country of residence, or to benefit from mandatory provisions of local consumer protection law.",
        },
      ],
    },
    {
      id: "governing-law",
      number: "14",
      title: "Governing Law and Jurisdiction",
      blocks: [
        {
          kind: "paragraph",
          text: "These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, United States of America, without regard to its conflict of law principles. The United Nations Convention on Contracts for the International Sale of Goods does not apply. To the extent that litigation is permitted under these Terms, you consent to the exclusive jurisdiction of the state and federal courts located in Sussex County, Delaware. Nothing in this Section 14 will override any mandatory rights you have as a consumer under the laws of your country of residence.",
        },
      ],
    },
    {
      id: "third-party-services",
      number: "15",
      title: "Third-Party Services and Links",
      blocks: [
        {
          kind: "paragraph",
          text: "The Platform may contain links to or integrate with third-party websites, services, or platforms, including but not limited to:",
        },
        {
          kind: "list",
          items: [
            "Stripe (payment processing);",
            "AI platforms (ChatGPT/OpenAI, Gemini/Google, Claude/Anthropic) for visibility testing;",
            "OTA platforms (Booking.com, Expedia, TripAdvisor) referenced in analyses.",
          ],
        },
        {
          kind: "paragraph",
          text: "We are not responsible for the content, privacy practices, or availability of any third-party services. Your use of third-party services is subject to their respective terms of service and privacy policies. We encourage you to review such terms before engaging with third-party services.",
        },
      ],
    },
    {
      id: "data-and-privacy",
      number: "16",
      title: "Data and Privacy",
      blocks: [
        {
          kind: "paragraph",
          text: "Your use of the Platform is also governed by our Privacy Policy, available at appearinaisearch.com/privacy. The Privacy Policy describes how we collect, use, store, and disclose your personal data. By using the Platform, you acknowledge the data practices described in the Privacy Policy.",
        },
        {
          kind: "paragraph",
          text: "We do not collect, store, or process any hotel guest personal data. The Platform analyzes only hotel website and marketing data that is publicly available or voluntarily submitted by you. Where you provide us with personal data of any individual other than yourself, you represent and warrant that you have the legal authority to do so.",
        },
      ],
    },
    {
      id: "electronic-communications",
      number: "17",
      title: "Electronic Communications",
      blocks: [
        {
          kind: "paragraph",
          text: "By creating an Account, you consent to receive electronic communications from us, including emails regarding your Account, service updates, product announcements, and, subject to your consent where required by applicable law (including the CAN-SPAM Act, GDPR e-Privacy rules, and the Telephone Consumer Protection Act for any SMS communications), promotional offers. You may opt out of promotional communications at any time by following the unsubscribe instructions in such emails or by contacting us. You may not opt out of transactional or service-related communications while you maintain an active Account.",
        },
      ],
    },
    {
      id: "general-provisions",
      number: "18",
      title: "General Provisions",
      blocks: [
        {
          kind: "paragraph",
          lead: "18.1 Entire Agreement.",
          text: "These Terms, together with the Privacy Policy and any order form or plan-specific terms, constitute the entire agreement between you and Sarucci Inc. with respect to the Platform and supersede all prior or contemporaneous communications, understandings, and agreements, whether written or oral.",
        },
        {
          kind: "paragraph",
          lead: "18.2 Severability.",
          text: "If any provision of these Terms is held to be invalid, illegal, or unenforceable, the remaining provisions shall continue in full force and effect, and the invalid provision shall be reformed to the minimum extent necessary to make it valid and enforceable while preserving the parties’ intent.",
        },
        {
          kind: "paragraph",
          lead: "18.3 Waiver.",
          text: "No waiver of any term or condition of these Terms shall be deemed a further or continuing waiver of such term or condition or any other term or condition. Any waiver must be in writing signed by the waiving party.",
        },
        {
          kind: "paragraph",
          lead: "18.4 Assignment.",
          text: "You may not assign or transfer these Terms or your rights hereunder, in whole or in part, by operation of law or otherwise, without our prior written consent. Any purported assignment in violation of this Section is null and void. We may freely assign these Terms without restriction.",
        },
        {
          kind: "paragraph",
          lead: "18.5 Force Majeure.",
          text: "Neither party shall be liable for any failure or delay in performing its obligations under these Terms (other than payment obligations) due to circumstances beyond its reasonable control, including but not limited to acts of God, natural disasters, war, terrorism, pandemics, government actions, power failures, internet disruptions, third-party service provider outages, or changes to third-party AI platforms.",
        },
        {
          kind: "paragraph",
          lead: "18.6 Headings.",
          text: "The section headings in these Terms are for convenience only and have no legal or contractual effect.",
        },
        {
          kind: "paragraph",
          lead: "18.7 No Agency.",
          text: "Nothing in these Terms creates any agency, partnership, joint venture, or employment relationship between you and Sarucci Inc.",
        },
        {
          kind: "paragraph",
          lead: "18.8 No Third-Party Beneficiaries.",
          text: "These Terms are for the sole benefit of you and Sarucci Inc. and do not create any rights in favor of any third party. The Contracts (Rights of Third Parties) Act 1999 (UK) and any similar law in another jurisdiction shall not apply to these Terms.",
        },
        {
          kind: "paragraph",
          lead: "18.9 Notices.",
          text: "Legal notices to us must be sent to Sarucci Inc., 16192 Coastal Highway, Lewes, Delaware 19958, USA, with a copy by email to support@sarucci.com. We may provide notices to you by email to the address on file for your Account, by posting to the Platform, or by other reasonable means. Notices are deemed effective upon receipt (or, for email, upon transmission absent bounce).",
        },
        {
          kind: "paragraph",
          lead: "18.10 Export Controls and Sanctions.",
          text: "You may not use, export, re-export, import, or transfer the Platform except as authorized by U.S. law, the laws of the jurisdiction in which you obtained the Platform, and any other applicable laws. You represent that you are not on any U.S. Government list of prohibited or restricted parties and are not located in a country subject to comprehensive U.S. sanctions.",
        },
        {
          kind: "paragraph",
          lead: "18.11 U.S. Government End Users.",
          text: "The Platform is a “commercial item,” as defined at 48 C.F.R. § 2.101. If you are a U.S. Government entity, the Platform is provided in accordance with 48 C.F.R. § 12.212 (for civilian agencies) or 48 C.F.R. §§ 227.7202-1 through 227.7202-4 (for Department of Defense agencies), and use, duplication, and disclosure are subject solely to the terms of this Agreement.",
        },
        {
          kind: "paragraph",
          lead: "18.12 Language.",
          text: "These Terms are drafted in English. If we provide a translation, the English version shall control in the event of any conflict, except where prohibited by law.",
        },
      ],
    },
    {
      id: "contact-information",
      number: "19",
      title: "Contact Information",
      blocks: [
        {
          kind: "paragraph",
          text: "If you have any questions, concerns, or feedback regarding these Terms, please contact us at:",
        },
        {
          kind: "address",
          lines: [
            "Sarucci Inc.",
            "AppearInAISearch",
            "16192 Coastal Highway",
            "Lewes, Delaware 19958",
            "Sussex County, United States",
            "Email: support@sarucci.com",
            "Website: appearinaisearch.com",
          ],
        },
      ],
    },
  ],
};
